"use client";

import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  signInWithCredential,
  signOut as firebaseSignOut,
  linkWithPopup,
  GoogleAuthProvider,
  type User,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { getFirestore, doc, onSnapshot, type Firestore } from "firebase/firestore";

// The one shared identity + entitlement store for the whole nohaya.com
// platform (CreatorVoice, AI Tools, Resumes, Places) — not specific to any
// single feature. Every feature backend checks the same `plan` field on
// users/{uid} here instead of reinventing its own billing/auth.
const firebaseConfig = {
  projectId: "nohaya-platform",
  appId: "1:895482471437:web:d7f938bef595cff705a144",
  storageBucket: "nohaya-platform.firebasestorage.app",
  apiKey: "AIzaSyAe1ormmXuQZjQphLzNTT9vIvHHPlMB4bA",
  authDomain: "nohaya-platform.firebaseapp.com",
  messagingSenderId: "895482471437",
};

let app: FirebaseApp | null = null;
let dbInstance: Firestore | null = null;

function getApp(): FirebaseApp {
  if (!app) {
    app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }
  return app;
}

export function getDb(): Firestore {
  if (!dbInstance) dbInstance = getFirestore(getApp());
  return dbInstance;
}

// Memoized so every caller (TtsStudioTool, every ProGate instance on a
// page with many of them, etc.) shares one in-flight sign-in instead of
// each racing its own onAuthStateChanged listener + signInAnonymously call
// — that race was the source of the "Missing or insufficient permissions"
// Firestore errors: a listener attached right after sign-in can fire
// before the new auth token has propagated to Firestore's credential
// provider. authStateReady() is Firebase's own primitive for waiting out
// that propagation instead of racing it.
let _signInPromise: Promise<User> | null = null;

export function ensureSignedIn(): Promise<User> {
  if (_signInPromise) return _signInPromise;
  const auth = getAuth(getApp());
  _signInPromise = (async () => {
    await auth.authStateReady();
    if (auth.currentUser) return auth.currentUser;
    const cred = await signInAnonymously(auth);
    return cred.user;
  })();
  return _signInPromise;
}

// Used by the "Upgrade to Nohaya Pro" flow. If the caller is already
// signed in anonymously, link the Google identity onto that same UID so
// their existing usage/history carries over instead of starting fresh.
export async function signInWithGoogle(): Promise<User> {
  const auth = getAuth(getApp());
  const provider = new GoogleAuthProvider();
  let user: User;
  try {
    user = auth.currentUser?.isAnonymous
      ? (await linkWithPopup(auth.currentUser, provider)).user
      : (await signInWithPopup(auth, provider)).user;
  } catch (e) {
    // This Google account is already linked to a different uid — e.g. the
    // user previously signed in on another device, which has its own
    // separate anonymous-then-linked account. Linking the same Google
    // identity to a second uid is impossible by design (one Google
    // identity maps to exactly one Firebase account), so instead sign
    // into that pre-existing account directly using the credential
    // Firebase attaches to this specific error.
    if (e instanceof FirebaseError && e.code === "auth/credential-already-in-use") {
      const credential = GoogleAuthProvider.credentialFromError(e);
      if (!credential) throw e;
      user = (await signInWithCredential(auth, credential)).user;
    } else {
      throw e;
    }
  }
  // Replace the cached sign-in so isAnonymous checks and future
  // getIdToken()/watchUser() calls reflect the upgraded identity instead
  // of the stale anonymous one ensureSignedIn() returned earlier.
  _signInPromise = Promise.resolve(user);
  return user;
}

// After signing out, the next ensureSignedIn() call must start a fresh
// anonymous session rather than returning the now-stale cached user.
export async function signOut(): Promise<void> {
  await firebaseSignOut(getAuth(getApp()));
  _signInPromise = null;
}

export async function getIdToken(): Promise<string> {
  const user = await ensureSignedIn();
  return user.getIdToken();
}

// For UI that needs to reactively know "is this a real, signed-in account"
// (e.g. swapping "Get Started" for "My Documents" in the header) rather
// than just resolving it once.
export function subscribeAuthState(onChange: (user: User | null) => void): () => void {
  return onAuthStateChanged(getAuth(getApp()), onChange);
}

export type PurchasedResume = { jobTitle: string; purchasedAt?: unknown; gateway?: string };

export type UserDoc = {
  plan?: "free" | "pro";
  quota?: Record<string, number>;
  proUsage?: Record<string, number>;
  // One-time resume-download purchases, keyed by `slug:resumeId`.
  purchasedResumes?: Record<string, PurchasedResume>;
};

export function watchUser(uid: string, onUpdate: (user: UserDoc) => void): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  function attach() {
    const ref = doc(getDb(), "users", uid);
    unsubscribe = onSnapshot(
      ref,
      (snap) => onUpdate((snap.exists() ? snap.data() : {}) as UserDoc),
      (err) => {
        // Firestore's listener stops retrying on its own after a
        // permission-denied (unlike transient network errors) — if this
        // was the auth-token-propagation race ensureSignedIn() guards
        // against, re-attach once auth is confirmed settled instead of
        // leaving the UI stuck with no plan data.
        if (cancelled || err.code !== "permission-denied") return;
        getAuth(getApp())
          .authStateReady()
          .then(() => {
            if (!cancelled) attach();
          });
      }
    );
  }

  attach();
  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}

export type JobDoc = {
  status: "pending" | "processing" | "completed" | "failed";
  progress?: number;
  resultUrl?: string;
  result?: Record<string, unknown>;
  error?: string;
};

export function watchJob(jobId: string, onUpdate: (job: JobDoc) => void): () => void {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  function attach() {
    const ref = doc(getDb(), "jobs", jobId);
    unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) onUpdate(snap.data() as JobDoc);
      },
      (err) => {
        // Same auth-token-propagation race watchUser() guards against —
        // without this, a listener attached right after a fresh sign-in
        // can hit one permission-denied and then sit dead with no retry.
        if (cancelled || err.code !== "permission-denied") return;
        getAuth(getApp())
          .authStateReady()
          .then(() => {
            if (!cancelled) attach();
          });
      }
    );
  }

  attach();
  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}
